import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import "./CrudForm.css"

function CrudForm({
  fields,
  defaultValues = {},
  onSubmit,
  submitText = "Save",
  onCancel,
}) {
  const emptyValues = useMemo(() => {
    const o = {};
    fields.forEach((f) => {
      if (f.type === "file") return;
      if (f.type === "checkbox") {
        o[f.name] = false;
        return;
      }
      o[f.name] = "";
    });
    return o;
  }, [fields]);

  const { register, handleSubmit, reset } = useForm({ defaultValues });

  const defaultsSnapshot = useMemo(
    () => JSON.stringify(defaultValues ?? {}),
    [defaultValues]
  );

  useEffect(() => {
    reset(defaultValues ?? {});
  }, [defaultsSnapshot, defaultValues, reset]);

  const submitHandler = async (data) => {
    await Promise.resolve(onSubmit(data));
    reset(emptyValues);
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)} className="crud-form">
      {fields.map((field) => {
        const rules = {
          required: field.required ? `${field.label} is required` : false,
        };
        if (field.type === "file" && field.maxFileMB) {
          rules.validate = (value) => {
            if (!value?.length) return true;
            const maxBytes = field.maxFileMB * 1024 * 1024;
            for (let i = 0; i < value.length; i += 1) {
              if (value[i].size > maxBytes) {
                return `Each file must be at most ${field.maxFileMB}MB`;
              }
            }
            return true;
          };
        }

        return (
          <div key={field.name} className="mb-3">
            {field.type !== "checkbox" && (
              <label className="form-label">
                {field.label}
                {field.required && <span className="text-danger"> *</span>}
              </label>
            )}

            {field.type === "textarea" ? (
              <textarea
                className="form-control"
                rows={field.rows || 4}
                {...register(field.name, rules)}
              />
            ) : field.type === "select" ? (
              <select className="form-select" {...register(field.name, rules)}>
                <option value="">
                  {field.placeholder || "—"}
                </option>
                {(field.options || []).map((opt) => (
                  <option key={String(opt.value)} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : field.type === "checkbox" ? (
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`crud-${field.name}`}
                  {...register(field.name)}
                />
                <label
                  className="form-check-label"
                  htmlFor={`crud-${field.name}`}
                >
                  {field.checkboxLabel || field.label}
                </label>
              </div>
            ) : field.type === "file" ? (
              <input
                type="file"
                className="form-control"
                multiple={Boolean(field.multiple)}
                accept={
                  field.accept ||
                  "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                }
                {...register(field.name, rules)}
              />
            ) : (
              <input
                type={field.type || "text"}
                className="form-control"
                {...register(field.name, rules)}
              />
            )}
            {field.hint && (
              <div className="form-text">{field.hint}</div>
            )}
          </div>
        );
      })}
            <div></div>
      <button type="submit" className=" crud-form-submit-btn">
        {submitText}
      </button>
      {onCancel && (
        <button
          type="button"
          className="btn btn-outline-secondary ms-2"
          onClick={() => {
            reset(emptyValues);
            onCancel();
          }}
        >
          Cancel
        </button>
      )}
    </form>
  );
}

export default CrudForm;